import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRoute } from '@react-navigation/native';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useEmployeesStore, withSelfOwner } from '../../team/store/useEmployeesStore';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useGlobalFeatureFlags } from '../../shell/providers/GlobalFeatureFlagsProvider';
import { isGlobalFeatureOn } from '../../shell/feature-flags/globalFeatureFlagsService';
import {
  resolveCashSummary,
  subscribeCashSessionChangeSignalDebounced,
  applyCheckCommand,
  listChecksForBranch,
  type CashSessionRow,
  type CashSummary,
  type CheckRow,
} from '../../cash/infra-api';
import { useEffectiveBusinessCapabilities } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { newClientUuid } from '../../shared/newClientUuid';
import { buildCashOperatorOptions } from '../../cash/labels-api';
import { listActiveSuppliersLocal } from '../../catalog/infra-api';
import { getFiscalProfile } from '../../fiscal/infra-api';
import { pullExpensesWhenNeeded } from '../../sync/core/pullExpensesWhenNeeded';
import type { ExpenseSettleMethod } from '../components/expenseSettlementMethods';
import { isoDateToPaidAt } from '../components/expensePaymentFlow';
import { buildExpensesListCsv } from '../application/expensesListCsv'
import { buildExpenseSettlementsCsv } from '../application/expensesSettlementsCsv'
import { getExpenseById, listExpenseCategories, listExpensesForExport, settleExpense, voidSettlement, voidExpense } from '../infra/expensesRepo'
import { listBranchSettlementsExportLocalQuery } from '../infra/expensesRepoSettlementsLocal'
import { useExpensesListFlow } from '../application/useExpensesListFlow'
import { todayIsoDate } from '../infra/expenseListSql'
import type { ExpenseCategoryRow, ExpenseRow, ExpenseSettlementListRow } from '../domain/expenseTypes'
import type { ExpenseCategorySlug } from '../domain/expenseCategories';
import { expenseErpCopy } from '../domain/expenseErpCopy';
import { useExpensePaymentsHistory } from './hooks/useExpensePaymentsHistory';
import { useExpenseRegisterSubmit } from './hooks/useExpenseRegisterSubmit';
import { useExpenseBillsKpi, useExpensePaymentsKpi } from './hooks/useExpensesKpi';
import { shareOrDownloadCsv } from '../../shared/csv-core/infra/shareCsv';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { showError, showInfo, showSuccess } from '../../shared/ui/appToast';
import { getErrorMessage } from '../../shared/errorMessage';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { captureCatch } from '../../shared/sentry/sentryUser';
import type { ScreenRefreshResult } from '../../shared/ui/screenRefreshFeedback';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { formatExpenseDate, formatExpenseMoney } from '../domain/expenseLabels';

import { formatMoney } from '../../shared/format/public-api';
function resolveOperatorLabel(
  userId: string | null | undefined,
  employees: ReturnType<typeof useEmployeesStore.getState>['employees'],
  ownerAuthUserId: string | null | undefined,
  ownerEmail: string | null | undefined
): string | null {
  if (!userId) return null;
  return (
    buildCashOperatorOptions([userId], employees, ownerAuthUserId, ownerEmail)[0]?.label ?? null
  );
}

export function useExpensesScreen() {
  const { confirm, ConfirmModal } = useOperationalConfirm();
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const { user } = useAuthStore();
  const { entitlements } = usePlanEntitlements();
  const { flags } = useGlobalFeatureFlags();
  const { canView, canEdit, canVoid } = useBranchPermission('Expenses');
  const canCreate = useModulePermission('Expenses', 'create');
  const canPay = useModulePermission('Expenses', 'pay');
  const canExport = useModulePermission('Expenses', 'export');
  const canEndorseChecks = useModulePermission('Checks', 'endorse');
  const employeesRaw = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const employees = useMemo(() => withSelfOwner(employeesRaw, selfOwner), [employeesRaw, selfOwner]);
  const loadEmployees = useEmployeesStore((s) => s.loadEmployees);

  const moduleEnabled = isGlobalFeatureOn(flags, 'expenses_module');
  const organizationId = entitlements?.organization_id ?? null;
  const checksCapabilityOn = useEffectiveBusinessCapabilities().has('checks');
  const canPayWithCheck = canPay && canEndorseChecks && checksCapabilityOn;

  const [categories, setCategories] = useState<ExpenseCategoryRow[]>([]);
  const [suppliers, setSuppliers] = useState<Array<{ id: string; name: string; taxId: string | null }>>([]);
  // Libro IVA compras: captura de comprobante fiscal solo en orgs RI.
  const [fiscalCaptureEnabled, setFiscalCaptureEnabled] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [settleTarget, setSettleTarget] = useState<ExpenseRow | null>(null);
  const [portfolioChecks, setPortfolioChecks] = useState<CheckRow[]>([]);
  const [portfolioChecksLoading, setPortfolioChecksLoading] = useState(false);
  const [detailTarget, setDetailTarget] = useState<ExpenseRow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeCashSession, setActiveCashSession] = useState<CashSessionRow | null>(null);
  const [cashSummary, setCashSummary] = useState<CashSummary | null>(null);
  const [screenView, setScreenView] = useState<'expenses' | 'payments'>('expenses');
  const [exporting, setExporting] = useState(false);
  const openingDetailRef = useRef(false);

  // Deep link Inicio → «Registrar gasto»: abre el modal de registro una vez por token.
  const route = useRoute<any>();
  const consumedRegisterTokenRef = useRef<string | null>(null);
  useEffect(() => {
    const params = route.params as { openRegister?: boolean; focusToken?: string } | undefined;
    if (params?.openRegister !== true) return;
    const token = params.focusToken ?? 'openRegister';
    if (token === consumedRegisterTokenRef.current) return;
    consumedRegisterTokenRef.current = token;
    setRegisterOpen(true);
  }, [route.params]);

  const listFlow = useExpensesListFlow({
    branchId: currentBranchId,
    moduleEnabled,
  });

  const paymentsFlow = useExpensePaymentsHistory(
    currentBranchId,
    moduleEnabled && screenView === 'payments'
  );

  const expenseKpi = useExpenseBillsKpi({
    moduleEnabled,
    listQuery: listFlow.listQuery,
  });

  const paymentsKpi = useExpensePaymentsKpi({
    moduleEnabled: moduleEnabled && screenView === 'payments',
    query: paymentsFlow.exportQuery,
  });

  // Cartera de cheques para "pagar con cheque" (endoso Fase C).
  useEffect(() => {
    if (!settleTarget || !canPayWithCheck || !currentBranchId) {
      setPortfolioChecks([]);
      return;
    }
    let cancelled = false;
    setPortfolioChecksLoading(true);
    listChecksForBranch({ branchId: currentBranchId, statuses: ['in_portfolio'] })
      .then((rows) => {
        if (!cancelled) setPortfolioChecks(rows);
      })
      .catch((err) => {
        captureCatch('expenses', 'checks.list')(err);
        if (!cancelled) setPortfolioChecks([]);
      })
      .finally(() => {
        if (!cancelled) setPortfolioChecksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [settleTarget, canPayWithCheck, currentBranchId]);

  const resolveUserLabel = useCallback(
    (userId: string | null | undefined) =>
      resolveOperatorLabel(userId, employees, user?.id ?? null, user?.email ?? null),
    [employees, user?.id, user?.email]
  );

  const activeCashSessionId = activeCashSession?.id ?? null;

  const expectedCashInDrawer = useMemo(() => {
    if (!cashSummary?.activeSession) return null;
    return (
      (cashSummary.activeSession.openingAmount ?? 0) +
      (cashSummary.sessionCashTotal ?? 0) -
      (cashSummary.sessionOutflowsTotal ?? 0)
    );
  }, [cashSummary?.activeSession, cashSummary?.sessionCashTotal, cashSummary?.sessionOutflowsTotal]);

  const activeCashSessionInfo = useMemo(() => {
    if (!activeCashSession) return null;
    return {
      session: activeCashSession,
      operatorLabel: resolveOperatorLabel(
        activeCashSession?.openedBy,
        employees,
        user?.id ?? null,
        user?.email ?? null
      ),
    };
  }, [activeCashSession, employees, user?.id, user?.email]);

  const reloadMeta = useCallback(async (options?: { forceCash?: boolean }) => {
    if (!currentBranchId || !moduleEnabled) return;
    try {
      if (organizationId) {
        const cats = await listExpenseCategories(organizationId);
        setCategories(cats);
      }
      setSuppliers(await listActiveSuppliersLocal());
      getFiscalProfile(currentBranchId)
        .then((profile) => setFiscalCaptureEnabled(profile?.taxRegime === 'responsable_inscripto'))
        .catch(() => setFiscalCaptureEnabled(false));
      const cash = await resolveCashSummary(
        currentBranchId,
        options?.forceCash ? { force: true } : undefined
      );
      const session = cash.activeSession;
      setActiveCashSession(session ?? null);
      setCashSummary(cash);
    } catch (e) {
      presentUnexpectedFailure({ module: 'expenses', op: 'reload_meta', error: e });
    }
  }, [currentBranchId, moduleEnabled, organizationId]);

  useEffect(() => {
    void loadEmployees();
  }, [loadEmployees]);

  const reloadMetaRef = useRef(reloadMeta);
  reloadMetaRef.current = reloadMeta;
  const reloadExpensesRef = useRef(listFlow.reloadExpensesFromStart);
  reloadExpensesRef.current = listFlow.reloadExpensesFromStart;
  const paymentsFlowReloadRef = useRef(paymentsFlow.reload);
  paymentsFlowReloadRef.current = paymentsFlow.reload;
  const expenseKpiRefreshRef = useRef(expenseKpi.refresh);
  expenseKpiRefreshRef.current = expenseKpi.refresh;
  const paymentsKpiRefreshRef = useRef(paymentsKpi.refresh);
  paymentsKpiRefreshRef.current = paymentsKpi.refresh;
  const screenViewRef = useRef(screenView);
  screenViewRef.current = screenView;

  const [pullInFlight, setPullInFlight] = useState(false);
  const effectiveExpenseLoading = listFlow.expenseLoading || (pullInFlight && listFlow.expenses.length === 0);

  const reloadExpensesLocal = useCallback(async (): Promise<{ pullError?: string | null }> => {
    await reloadMetaRef.current();
    await reloadExpensesRef.current();
    void expenseKpiRefreshRef.current();
    void paymentsKpiRefreshRef.current();
    if (screenViewRef.current === 'payments') {
      return paymentsFlowReloadRef.current();
    }
    return {};
  }, []);

  const loadFn = useCallback(async (opts?: { force?: boolean }): Promise<ScreenRefreshResult | void> => {
    if (opts?.force) {
      const reloadResult = await reloadExpensesLocal();
      if (reloadResult.pullError) {
        return {
          partialMessage: 'No pude sincronizar los pagos. Estás viendo lo de esta compu.',
        };
      }
    } else {
      void reloadMetaRef.current();
      void reloadExpensesRef.current();
      if (screenView === 'payments') {
        void paymentsFlowReloadRef.current();
      }
      if (currentBranchId) {
        setPullInFlight(true);
        void pullExpensesWhenNeeded(currentBranchId)
          .then((result) => {
            if (result.pulled) {
              void reloadExpensesRef.current();
              void expenseKpiRefreshRef.current();
              void paymentsKpiRefreshRef.current();
              if (screenView === 'payments') {
                void paymentsFlowReloadRef.current();
              }
            }
          })
          .finally(() => setPullInFlight(false));
      }
    }
  }, [reloadExpensesLocal, screenView, currentBranchId]);

  useEffect(() => {
    return subscribeCashSessionChangeSignalDebounced(() => {
      void reloadMeta();
    });
  }, [reloadMeta]);

  /**
   * Pull remoto de gastos cuando currentBranchId se resuelve (bootstrap completo).
   * useFocusEffect puede disparar loadFn antes de que currentBranchId esté listo,
   * por lo que el pull se salta y nunca se recupera sin este efecto.
   */
  useEffect(() => {
    if (!currentBranchId || !moduleEnabled) return;
    let cancelled = false;
    setPullInFlight(true);
    void pullExpensesWhenNeeded(currentBranchId)
      .then((result) => {
        if (cancelled) return;
        if (result.pulled) {
          void reloadExpensesRef.current();
          void expenseKpiRefreshRef.current();
          void paymentsKpiRefreshRef.current();
          if (screenViewRef.current === 'payments') {
            void paymentsFlowReloadRef.current();
          }
        }
      })
      .finally(() => {
        if (!cancelled) setPullInFlight(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentBranchId, moduleEnabled]);

  useEffect(() => {
    if (registerOpen || settleTarget) {
      void reloadMeta({ forceCash: true });
    }
  }, [registerOpen, settleTarget, reloadMeta]);

  const { handleSubmit: handleCreate } = useExpenseRegisterSubmit({
    canEdit: canCreate,
    organizationId: organizationId ?? null,
    branchId: currentBranchId ?? null,
    activeCashSessionId,
    userId: user?.id ?? null,
    onSuccess: async () => {
      await reloadExpensesLocal();
    },
    onClose: () => setRegisterOpen(false),
    setSubmitting,
    expectedCashInDrawer,
    confirm,
  });

  const handleSettle = useCallback(
    async (input: {
      expense: ExpenseRow;
      method: ExpenseSettleMethod;
      amountText: string;
      note: string;
      paidDate: string;
      checkId?: string | null;
    }) => {
      if (!canPay || !currentBranchId) {
        if (!canPay) showError('Sin permisos', 'No podés pagar gastos.');
        return;
      }
      // Endoso Fase C: el cheque en cartera paga el gasto (sin egreso de caja).
      if (input.method === 'check') {
        if (!input.checkId) {
          showError('Cheque', 'Elegí el cheque a endosar.');
          return;
        }
        setSubmitting(true);
        try {
          await applyCheckCommand({
            operation: 'endorse',
            checkId: input.checkId,
            settlementId: newClientUuid(),
            expenseId: input.expense.id,
            note: input.note.trim() || undefined,
            operationId: newClientUuid(),
          });
          setSettleTarget(null);
          setDetailTarget(null);
          showSuccess('Gasto pagado con cheque');
          await reloadExpensesLocal();
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'No pude endosar el cheque.';
          presentUnexpectedFailure({ module: 'expenses', op: 'endorse_check', error, userMessage: message });
        } finally {
          setSubmitting(false);
        }
        return;
      }
      const amount = parseDecimalInput(input.amountText, { min: 0.01 });
      if (amount == null) {
        showError('Monto inválido', 'Ingresá un monto mayor a cero.');
        return;
      }
      if (input.method === 'cash_drawer' && !activeCashSessionId) {
        showError('Caja cerrada', 'Abrí un turno de caja para pagar en efectivo desde la caja.');
        return;
      }
      if (input.method === 'other' && !input.note.trim()) {
        showError('Nota requerida', 'Indicá cuándo y cómo se pagó.');
        return;
      }
      if (
        input.method === 'cash_drawer' &&
        expectedCashInDrawer != null &&
        amount > expectedCashInDrawer
      ) {
        const ok = await confirm({
          title: 'Sale más plata de la que hay en caja',
          message: `Querés sacar ${formatMoney(amount)} y en caja hay ${formatMoney(expectedCashInDrawer)}. ¿Lo registrás igual?`,
          confirmLabel: 'Registrar',
        });
        if (!ok) return;
      }
      const categorySlug = (input.expense.categorySlug ?? 'other') as ExpenseCategorySlug;
      setSubmitting(true);
      try {
        await settleExpense({
          expenseId: input.expense.id,
          branchId: currentBranchId,
          method: input.method,
          amount,
          categorySlug,
          cashSessionId: input.method === 'cash_drawer' ? activeCashSessionId : null,
          note: input.note.trim() || null,
          paidAt:
            input.method === 'cash_drawer'
              ? undefined
              : isoDateToPaidAt(input.paidDate || todayIsoDate()),
          createdBy: user?.id ?? null,
        });
        setSettleTarget(null);
        setDetailTarget(null);
        showSuccess('Pago registrado');
        await reloadExpensesLocal();
      } catch (error: unknown) {
        presentUnexpectedFailure({ module: 'expenses', op: 'register_payment', error, userMessage: 'No pude registrar el pago.' });
      } finally {
        setSubmitting(false);
      }
    },
    [canPay, currentBranchId, activeCashSessionId, expectedCashInDrawer, confirm, user?.id, reloadExpensesLocal]
  );

  const handleVoid = useCallback(
    async (expense: ExpenseRow, voidReason: string) => {
      if (!canVoid || !currentBranchId) {
        showError('Sin permisos', 'No podés anular gastos.');
        return;
      }
      if (expense.purchaseOrderId) {
        showError('Usá Compras', expenseErpCopy.purchaseLinkedBillVoidBlocked);
        return;
      }
      setSubmitting(true);
      try {
        await voidExpense({
          expenseId: expense.id,
          branchId: currentBranchId,
          voidedBy: user?.id,
          voidReason,
        });
        setDetailTarget(null);
        showSuccess('Gasto anulado', 'Quedó anulado.');
        await reloadExpensesLocal();
      } catch (error: unknown) {
        presentUnexpectedFailure({ module: 'expenses', op: 'void_expense', error, userMessage: 'No pude anularlo.' });
      } finally {
        setSubmitting(false);
      }
    },
    [canVoid, currentBranchId, user?.id, reloadExpensesLocal]
  );

  const handleVoidSettlement = useCallback(
    async (settlementId: string, voidReason: string) => {
      if (!canVoid || !currentBranchId) {
        showError('Sin permisos', 'No podés anular pagos.');
        return;
      }
      setSubmitting(true);
      try {
        await voidSettlement({
          settlementId,
          branchId: currentBranchId,
          voidedBy: user?.id ?? null,
          voidReason,
        });
        showSuccess('Pago anulado', 'Quedó anulado.');
        await reloadExpensesLocal();
        const detailId = detailTarget?.id;
        if (detailId) {
          const next = await getExpenseById(detailId, { branchId: currentBranchId });
          // Si el cache todavía no trajo la fila, conservar la anterior — no cerrar el modal.
          setDetailTarget((prev) => next ?? prev);
        }
      } catch (error: unknown) {
        presentUnexpectedFailure({ module: 'expenses', op: 'void_payment', error, userMessage: 'No pude anular el pago.' });
      } finally {
        setSubmitting(false);
      }
    },
    [canVoid, currentBranchId, user?.id, reloadExpensesLocal, detailTarget?.id]
  );

  const handleOpenDetail = useCallback((item: ExpenseRow) => {
    if (openingDetailRef.current) return;
    openingDetailRef.current = true;
    setDetailTarget(item);
    setTimeout(() => {
      openingDetailRef.current = false;
    }, 400);
  }, []);

  const handleOpenPaymentFromHistory = useCallback(
    async (row: ExpenseSettlementListRow) => {
      if (openingDetailRef.current) return;
      openingDetailRef.current = true;
      try {
        const expense = await getExpenseById(row.expenseId, { branchId: currentBranchId });
        if (expense) {
          setDetailTarget(expense);
          return;
        }
        showInfo(
          'Esta factura no está acá',
          `${row.categoryName} · ${formatExpenseDate(row.expenseDate)} · ${formatExpenseMoney(row.expenseAmount)}\n\nSe cargó en otra compu o celular. Conectate para traerla.`
        );
      } finally {
        openingDetailRef.current = false;
      }
    },
    [currentBranchId]
  );

  const handleExportPaymentsCsv = useCallback(async () => {
    if (!canExport) {
      showError('Sin permisos', 'No podés exportar gastos.');
      return;
    }
    if (!paymentsFlow.exportQuery || exporting) return;
    const paymentsPeriodTag =
      paymentsFlow.periodPreset === 'all'
        ? 'todos'
        : `${paymentsFlow.exportQuery.fromIso.slice(0, 10)}_${paymentsFlow.exportQuery.toIso.slice(0, 10)}`;
    const filename = `pagos_gastos_${paymentsPeriodTag}.csv`;
    setExporting(true);
    try {
      const { rows, truncated } = await listBranchSettlementsExportLocalQuery(paymentsFlow.exportQuery);
      if (rows.length === 0) {
        showInfo('Exportar', 'Con estos filtros no hay pagos para exportar.');
        return;
      }
      const csv = buildExpenseSettlementsCsv(rows);
      const outcome = await shareOrDownloadCsv(csv, filename);
      if (outcome === 'ok') {
        showSuccess('CSV descargado', filename);
      }
      if (truncated) {
        showInfo('Exportar', 'Exporté hasta 5000 filas de este filtro.');
      }
    } catch (e) {
      console.warn('[useExpensesScreen] export payments csv', e);
      presentUnexpectedFailure({ module: 'expenses', op: 'export_payments_csv', error: e, userMessage: 'No pude generar el archivo.' });
    } finally {
      setExporting(false);
    }
  }, [canExport, paymentsFlow.exportQuery, exporting]);

  const handleExportCsv = useCallback(async () => {
    if (!canExport) {
      showError('Sin permisos', 'No podés exportar gastos.');
      return;
    }
    if (!listFlow.listQuery || exporting) return;
    const expensesPeriodTag =
      listFlow.periodPreset === 'all'
        ? 'todos'
        : `${listFlow.listQuery.fromDate}_${listFlow.listQuery.toDate}`;
    const filename = `gastos_${expensesPeriodTag}.csv`;
    setExporting(true);
    try {
      const { rows, truncated } = await listExpensesForExport(listFlow.listQuery);
      if (rows.length === 0) {
        showInfo('Exportar', 'Con estos filtros no hay gastos para exportar.');
        return;
      }
      const csv = buildExpensesListCsv(rows);
      const outcome = await shareOrDownloadCsv(csv, filename);
      if (outcome === 'ok') {
        showSuccess('CSV descargado', filename);
      }
      if (truncated) {
        showInfo('Exportar', 'Exporté hasta 5000 filas de este filtro.');
      }
    } catch (e) {
      console.warn('[useExpensesScreen] export csv', e);
      presentUnexpectedFailure({ module: 'expenses', op: 'export_csv', error: e, userMessage: 'No pude generar el archivo.' });
    } finally {
      setExporting(false);
    }
  }, [canExport, listFlow.listQuery, exporting]);

  return {
    canView,
    canEdit: canCreate,
    canPay,
    canExport,
    canVoid,
    moduleEnabled,
    organizationId,
    currentBranchId,
    userId: user?.id ?? null,
    activeCashSessionId,
    activeCashSessionInfo,
    categories,
    suppliers,
    fiscalCaptureEnabled,
    loadFn,
    registerOpen,
    setRegisterOpen,
    settleTarget,
    setSettleTarget,
    canPayWithCheck,
    portfolioChecks,
    portfolioChecksLoading,
    detailTarget,
    setDetailTarget,
    submitting,
    handleCreate,
    handleSettle,
    handleVoid,
    handleVoidSettlement,
    handleExportCsv,
    handleExportPaymentsCsv,
    handleOpenDetail,
    handleOpenPaymentFromHistory,
    exporting,
    resolveUserLabel,
    screenView,
    setScreenView,
    paymentsFlow,
    listFlow,
    expenseKpi,
    paymentsKpi,
    effectiveExpenseLoading,
    ConfirmModal,
  };
}

export type ExpensesScreenViewModel = ReturnType<typeof useExpensesScreen>;

// Re-export types used by settle modal validation helpers
export type { ExpensePaidFormChoice, ExpensePaymentStatusChoice } from '../components/expensePaymentFlow';
